import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { typedInfiniteQuery, typedMutation, typedQuery } from "shared/api/types/typedQueries";
import useToastes from 'shared/hooks/useToastes';
import useTypedHistory from 'shared/hooks/useTypedHistory';
import { PathsE, PathsParams, LayoutsE } from 'shared/routes';
import { UsersT, ChangeStatusUserT, CreateUserT, DeleteUserT, OneUserT, UpdateUserT } from '../types/users';
import { instance } from "./common";
import { SERVICES_KEY } from "./services";

const BASE_URL = 'users'

const USERS_KEY = 'USERS_KEY';
const ONE_USER_KEY = 'ONE_USER_KEY';

export const useGetUsers = typedInfiniteQuery<UsersT>(({ params }) => (
  useInfiniteQuery({
    queryKey: [USERS_KEY, params],
    queryFn: () => instance.get<unknown, UsersT['response']>(BASE_URL, { params }),
    initialPageParam: 1,
    getNextPageParam: () => null,
    getPreviousPageParam: () => null,
  })
));

export const useGetOneUser = typedQuery<OneUserT>(({ pathParams: { userId } }) => (
  useQuery({
    queryKey: [ONE_USER_KEY, userId],
    queryFn: () => instance.get(`${BASE_URL}/${userId}`),
    enabled: !!userId,
  })
));

export const useCreateUser = typedMutation<CreateUserT>(() => {
  const queryCache = useQueryClient();
  const { successToast } = useToastes();
  const push = useTypedHistory()
  return (
    useMutation({
      mutationFn: (data) => instance.post(BASE_URL, data),
      onSuccess: () => {
        successToast('User was successfully created');
        push(PathsE.USERS);
        queryCache.invalidateQueries({ queryKey: [USERS_KEY] });
      }
    })
  )
});

export const useUpdateOneUser = typedMutation<UpdateUserT>(({ pathParams: { userId } }) => {
  const { successToast } = useToastes();
  const queryCache = useQueryClient()
  const push = useTypedHistory<PathsParams[PathsE.SHOW_USER]>();
  return (
    useMutation({
      mutationFn: (data) => instance.put(`${BASE_URL}/${userId}`, data),
      onSuccess: () => {
        successToast('User was successfully updated');
        queryCache.invalidateQueries({ queryKey: [USERS_KEY] });
        queryCache.invalidateQueries({ queryKey: [ONE_USER_KEY, userId] })
        queryCache.invalidateQueries({ queryKey: [SERVICES_KEY] })
        push(PathsE.USERS, LayoutsE.DASHBOARD)
      }
    })
  )
});

export const useActivateOneUser = typedMutation<ChangeStatusUserT>(() => {
  const { successToast } = useToastes();
  const queryCache = useQueryClient()
  return (
    useMutation({
      mutationFn: ({ id }) => instance.put(`${BASE_URL}/${id}/toggle`),
      onSuccess: (_, { id }) => {
        successToast('User was successfully updated');
        queryCache.invalidateQueries({ queryKey: [USERS_KEY] });
        queryCache.invalidateQueries({ queryKey: [ONE_USER_KEY, id] });
      }
    })
  )
});

export const useDeactivateOneUser = typedMutation<ChangeStatusUserT>(() => {
  const { successToast } = useToastes();
  const queryCache = useQueryClient()
  return (
    useMutation({
      mutationFn: ({ id }) => instance.put(`${BASE_URL}/${id}/toggle`),
      onSuccess: (_, { id }) => {
        successToast('User was successfully updated');
        queryCache.invalidateQueries({ queryKey: [USERS_KEY] });
        queryCache.invalidateQueries({ queryKey: [ONE_USER_KEY, id] });
      }
    })
  )
});

export const useDeleteUser = typedMutation<DeleteUserT>(() => {
  const { successToast } = useToastes();
  const queryCache = useQueryClient()
  const push = useTypedHistory()
  return (
    useMutation({
      mutationFn: ({ userId }) => instance.delete(`${BASE_URL}/${userId}`),
      onSuccess: () => {
        successToast('User was successfully deleted');
        queryCache.invalidateQueries({ queryKey: [USERS_KEY] });
        push(PathsE.USERS)
      }
    })
  )
});
export const useRestoreUser = typedMutation<DeleteUserT>(() => {
  const { successToast } = useToastes();
  const queryCache = useQueryClient()
  return (
    useMutation({
      mutationFn: ({ userId }) => instance.put(`${BASE_URL}/${userId}/restore`),
      onSuccess: () => {
        successToast('User was successfully restored');
        queryCache.invalidateQueries({ queryKey: [USERS_KEY] });
      }
    })
  )
});
